import { AlertTriangle, CheckCircle2, Info, XCircle } from 'lucide-react'
import { useEffect, useState, type CSSProperties } from 'react'
import { Toaster as Sonner, type ToasterProps as SonnerProps } from 'sonner'
import { Spinner } from './Spinner'
import { useToastsLifted } from './toastLift'

function readTheme(): 'light' | 'dark' {
  if (typeof document === 'undefined') return 'light'
  const el = document.documentElement
  return el.dataset.theme === 'dark' || el.classList.contains('dark') ? 'dark' : 'light'
}

function useDocumentTheme() {
  const [theme, setTheme] = useState(readTheme)
  useEffect(() => {
    const observer = new MutationObserver(() => setTheme(readTheme()))
    observer.observe(document.documentElement, { attributes: true, attributeFilter: ['data-theme', 'class'] })
    return () => observer.disconnect()
  }, [])
  return theme
}

function useMedia(query: string) {
  const [match, setMatch] = useState(() => typeof window !== 'undefined' && !!window.matchMedia?.(query).matches)
  useEffect(() => {
    const mql = window.matchMedia?.(query)
    if (!mql) return
    const onChange = () => setMatch(mql.matches)
    mql.addEventListener('change', onChange)
    return () => mql.removeEventListener('change', onChange)
  }, [query])
  return match
}

const vars = {
  '--normal-bg': 'var(--color-surface)',
  '--normal-border': 'var(--color-line)',
  '--normal-text': 'var(--color-text)',
  '--border-radius': '12px',
  '--width': '360px',
} as CSSProperties

/** App wide toasts (sonner): bottom right on desktop, top center on mobile, themed with MG tokens. */
export function Toaster(props: Partial<SonnerProps>) {
  const theme = useDocumentTheme()
  const mobile = useMedia('(max-width: 639px)')
  // platform-account-3: above a showing save bar (toastLift), 16 px off the bottom from 768 px up, above the bottom nav below.
  const wide = useMedia('(min-width: 768px)')
  const lifted = useToastsLifted()
  return (
    <Sonner
      theme={theme}
      position={mobile ? 'top-center' : 'bottom-right'}
      gap={10}
      offset={mobile ? 12 : lifted ? { bottom: wide ? 96 : 152 } : 24}
      closeButton={false}
      style={vars}
      icons={{
        success: <CheckCircle2 className="size-[18px] text-success" strokeWidth={2} />,
        error: <XCircle className="size-[18px] text-danger" strokeWidth={2} />,
        warning: <AlertTriangle className="size-[18px] text-warning" strokeWidth={2} />,
        info: <Info className="size-[18px] text-accent-strong" strokeWidth={2} />,
        loading: <Spinner size={18} label="" className="text-muted" />,
      }}
      toastOptions={{
        classNames: {
          toast: 'font-sans! shadow-pop! border-line! gap-2.5! items-start! text-body!',
          title: 'font-semibold! text-text!',
          description: 'text-meta! text-muted!',
          actionButton: 'bg-primary! text-on-primary! rounded-lg! font-semibold!',
          cancelButton: 'bg-surface-2! text-text! rounded-lg!',
          icon: 'mt-px!',
        },
      }}
      {...props}
    />
  )
}
